/**
 * Lykke's panel in the new layout: one agent on every screen, left of the workspace.
 *
 * - The whole conversation (the person's Lykke thread, the same on every page) with where they are in the header.
 *   Each ask carries the page and its object (pageContext.tsx), so "this campaign" or "this deck" is understood.
 * - A page's own agent work as a mode of hers (the Deck studio's deck chat, Order a campaign's writer): the page
 *   puts it in through AgentPanelSlot; the panel opens on it and "Lykke" goes back to the whole conversation.
 * - Beatly staff also reach their staff agents here (the guide, the agent team), not in a second window.
 *
 * Retractable (remembered per person), resizable on desktop (320 to 560 px, remembered), a bottom sheet on a phone.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ChevronDown, History, PanelLeftClose, X } from 'lucide-react';
import { cn } from '@/components/ui/utils';
import { useLykke, HOME } from '@/cowork/lykke/LykkeContext';
import { LykkeConversation } from '@/cowork/lykke/LykkeConversation';
import { LykkeAvatar } from '@/cowork/lykke/LykkeAvatar';
import { actionable, NeedsYouList, useNeedsYou } from '@/cowork/lykke/NeedsYou';
import { GuideConversation } from '@/cowork/guide/GuidePanel';
import { useGuide } from '@/cowork/guide/GuideContext';
import { AgentRail } from '@/cowork/rail/AgentRail';
import { useAgentRail } from '@/cowork/rail/AgentRailContext';
import { parseAppPath } from '@/routes';
import { entryFor } from '@/cowork/guide/pageMap';
import { useShell } from './ShellContext';
import { AGENT_MAX_W, AGENT_MIN_W } from './layoutPref';
import { ConversationsDrawer } from './ConversationsDrawer';
import type { AgentComposerHandle } from './AgentComposer';

/** Where the person is, in words, for the header ("Campaign brief"). */
function useWhere(): string {
  const loc = useLocation();
  const p = parseAppPath(loc.pathname);
  return entryFor(loc.pathname, p.currentPage, p.subTab)?.label || '';
}

export function AgentPanel({ staff }: { staff: boolean }) {
  const shell = useShell();
  const l = useLykke();
  const guide = useGuide();
  const rail = useAgentRail();
  const loc = useLocation();
  const where = useWhere();
  const route = parseAppPath(loc.pathname);
  const campaignId = route.currentPage === 'see-campaigns' ? route.campaignId || null : null;
  const needs = useNeedsYou(!!l?.available && !!campaignId, campaignId);
  const open = actionable(needs.data);
  const [showNeeds, setShowNeeds] = useState(false);
  const [width, setWidth] = useState<number | null>(null);
  // Phone: the tap that opened the sheet must not land on its call button (it sits where the "Ask Lykke" button was).
  const [tapGuard, setTapGuard] = useState(false);
  const phoneOpen = !!shell?.isPhone && !!shell?.agentOpen;
  useEffect(() => { if (!phoneOpen) return; setTapGuard(true); const t = setTimeout(() => setTapGuard(false), 450); return () => clearTimeout(t); }, [phoneOpen]);
  const dragging = useRef(false);

  // Stable ref callbacks for the mode slots (a new one each render would detach and attach the slot forever).
  const setSlot = shell?.setSlot;
  const slotRefs = useRef(new Map<string, (el: HTMLDivElement | null) => void>());
  const slotRef = (id: string) => {
    let fn = slotRefs.current.get(id);
    if (!fn) { fn = (el) => setSlot?.(id, el); slotRefs.current.set(id, fn); }
    return fn;
  };

  const registerFocus = shell?.registerFocus;
  const onComposer = useCallback((c: AgentComposerHandle | null) => { if (c) registerFocus?.(() => c.focus()); }, [registerFocus]);

  // Ctrl/Cmd+J opens and closes the panel from anywhere.
  const setAgentOpen = shell?.setAgentOpen; const agentOpen = !!shell?.agentOpen;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'j') { e.preventDefault(); setAgentOpen?.(!agentOpen); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [agentOpen, setAgentOpen]);

  if (!shell) return null;
  const { view, setView, modes, isPhone, prefs } = shell;
  const engines = [
    { id: 'lykke', label: 'Lykke' },
    ...modes.map((m) => ({ id: m.id, label: m.label })),
    ...(staff && guide?.available ? [{ id: 'guide', label: 'Guide' }] : []),
    ...(staff && rail.available ? [{ id: 'agents', label: 'Agents' }] : []),
  ];
  const mode = modes.find((m) => m.id === view);
  const current = engines.some((e) => e.id === view) ? view : 'lykke';
  const live = l?.voice !== 'off' && !!l?.voice;
  const line = mode ? mode.line || mode.label : current === 'guide' ? 'Staff guide: moves the app, runs page actions' : current === 'agents' ? 'The agent team (staff)' : where ? `With you on ${where}` : 'With you on every page';
  const w = width ?? prefs.agentWidth;

  const startDrag = (e: React.PointerEvent) => {
    if (isPhone) return;
    e.preventDefault();
    dragging.current = true;
    const startX = e.clientX; const startW = w;
    const move = (ev: PointerEvent) => { if (dragging.current) setWidth(Math.min(AGENT_MAX_W, Math.max(AGENT_MIN_W, startW + ev.clientX - startX))); };
    const up = (ev: PointerEvent) => {
      dragging.current = false;
      window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up);
      shell.setPrefs({ agentWidth: Math.min(AGENT_MAX_W, Math.max(AGENT_MIN_W, startW + ev.clientX - startX)) });
      setWidth(null);
    };
    window.addEventListener('pointermove', move); window.addEventListener('pointerup', up);
  };

  return (
    <section
      aria-label="Lykke"
      data-agent-panel
      data-open={shell.agentOpen ? 'true' : 'false'}
      style={isPhone ? undefined : { width: w }}
      className={cn('flex min-h-0 flex-col overflow-hidden bg-[var(--shell-surface)] text-[var(--shell-text)]', !shell.agentOpen && 'hidden', tapGuard && 'pointer-events-none',
        isPhone
          ? 'fixed inset-x-0 bottom-0 z-[70] h-[66dvh] rounded-t-[20px] border-t border-[var(--shell-border)] shadow-[0_-16px_48px_rgba(16,24,40,0.22)]'
          : 'relative h-full shrink-0 rounded-[16px] border border-[var(--shell-border)]')}
    >
      {isPhone && <div className="mx-auto mt-2 h-1 w-10 shrink-0 rounded-full bg-[var(--shell-border-strong)]" aria-hidden />}
      <header className="flex items-center gap-2.5 px-3.5 pb-2 pt-3">
        <LykkeAvatar size={32} live={live} />
        <div className="flex min-w-0 flex-1 flex-col leading-tight">
          <span className="text-[14px] font-semibold">Lykke</span>
          <span className="truncate text-[12px] text-[var(--shell-muted)]" data-agent-where>{line}</span>
        </div>
        <button type="button" onClick={() => shell.openDrawer({ id: 'conversations', title: 'Your conversations', content: <ConversationsDrawer /> })} aria-label="Your conversations" title="Your conversations"
          className="grid h-8 w-8 place-items-center rounded-full text-[var(--shell-muted)] hover:bg-[var(--shell-hover)] hover:text-[var(--shell-text)]"><History className="h-4 w-4" /></button>
        <button type="button" onClick={() => shell.setAgentOpen(false)} aria-label={isPhone ? 'Close Lykke' : 'Hide Lykke'} title={isPhone ? 'Close' : 'Hide Lykke (Ctrl J)'}
          className="grid h-8 w-8 place-items-center rounded-full text-[var(--shell-muted)] hover:bg-[var(--shell-hover)] hover:text-[var(--shell-text)]">
          {isPhone ? <X className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
        </button>
      </header>

      {engines.length > 1 && (
        <div role="tablist" aria-label="What Lykke works on" className="mx-3.5 mb-2 flex gap-0.5 rounded-xl bg-[var(--shell-hover)] p-0.5">
          {engines.map((e) => (
            <button key={e.id} type="button" role="tab" aria-selected={current === e.id} onClick={() => setView(e.id)}
              className={cn('min-w-0 flex-1 truncate rounded-[10px] px-2 py-1 text-[12.5px] font-semibold transition-colors',
                current === e.id ? 'bg-[var(--shell-surface)] text-[var(--shell-text)] shadow-[0_1px_2px_rgba(16,24,40,0.08)]' : 'text-[var(--shell-muted)] hover:text-[var(--shell-text)]')}>
              {e.label}
            </button>
          ))}
        </div>
      )}

      <div className={cn('flex min-h-0 flex-1 flex-col', current !== 'lykke' && 'hidden')} data-agent-view="lykke">
        {campaignId && open.length > 0 && (
          <div className="mx-3.5 mb-1 rounded-xl border border-[var(--shell-border)] px-3 py-2">
            <button type="button" onClick={() => setShowNeeds((v) => !v)} aria-expanded={showNeeds} className="flex w-full items-center gap-2 text-left text-[12.5px] font-semibold">
              <span className="grid h-5 min-w-5 place-items-center rounded-full bg-[#be43ff] px-1.5 text-[11px] font-bold text-white">{open.length}</span>
              {open.length === 1 ? 'thing needs you on this campaign' : 'things need you on this campaign'}
              <ChevronDown className={cn('ml-auto h-4 w-4 text-[var(--shell-muted)] transition-transform', showNeeds && 'rotate-180')} />
            </button>
            {showNeeds && <div className="mt-2"><NeedsYouList items={open} compact hideCampaign /></div>}
          </div>
        )}
        {route.currentPage === 'lykke' && !route.campaignId ? (
          <p className="mx-3.5 rounded-xl bg-[var(--shell-hover)] px-3 py-2.5 text-[13px] text-[var(--shell-muted)]" data-agent-on-page>This conversation is open on the page. It follows you here on every other page.</p>
        ) : l?.available ? (
          <LykkeConversation scope={HOME} variant="dock" placeholder={campaignId ? 'Ask about this campaign, or anything' : 'Ask Lykke anything'}
            emptyHint="Ask about your campaigns, drafts, results or brand. She can also take you to any page and start the work there."
            composerRef={onComposer} />
        ) : (
          <p className="px-4 py-3 text-[13px] text-[var(--shell-muted)]">Sign in to talk to Lykke.</p>
        )}
      </div>

      {modes.map((m) => (
        <div key={m.id} ref={slotRef(m.id)} className={cn('flex min-h-0 flex-1 flex-col', current !== m.id && 'hidden')} data-agent-view={m.id} />
      ))}
      {staff && guide?.available && current === 'guide' && <GuideConversation className="flex-1" autoFocus />}
      {staff && rail.available && current === 'agents' && <AgentRail inPanel />}

      {!isPhone && (
        <div role="separator" aria-orientation="vertical" aria-label="Resize Lykke" title="Drag to resize" onPointerDown={startDrag}
          className="absolute inset-y-3 right-0 w-1.5 cursor-col-resize rounded-full transition-colors hover:bg-[#be43ff]/30" />
      )}
    </section>
  );
}
